/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback, useEffect, useState, useRef } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes-react';
import {
  KnativeRevision,
  KNATIVE_SERVICE_ANNOTATION,
  KNATIVE_NAMESPACE_ANNOTATION,
} from '../types';

export interface UseKnativeRevisionsResult {
  revisions: KnativeRevision[];
  loading: boolean;
  error: Error | undefined;
  refresh: () => void;
}

/**
 * Hook to fetch Knative Revisions for the current entity's service.
 *
 * @param clusterName - Optional cluster name to query.
 * @returns Knative Revisions data, loading state, and error
 *
 * @public
 */
export function useKnativeRevisions(
  clusterName?: string,
  refreshIntervalMs: number = 0,
): UseKnativeRevisionsResult {
  const { entity } = useEntity();
  const kubernetesApi = useApi(kubernetesApiRef);

  const [revisions, setRevisions] = useState<KnativeRevision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);
  const hasLoadedRef = useRef(false);

  const serviceName = entity.metadata.annotations?.[KNATIVE_SERVICE_ANNOTATION];
  const serviceNamespace =
    entity.metadata.annotations?.[KNATIVE_NAMESPACE_ANNOTATION] ||
    entity.metadata.namespace ||
    'default';

  // 从 Component 注解读取集群名称
  const getClusterFromAnnotations = (): string | undefined => {
    const annotations = entity.metadata.annotations || {};
    if (clusterName) return clusterName;
    // 从 template 生成的注解中读取集群名称
    return annotations['backstage.io/kubernetes-cluster-name'];
  };

  const fetchRevisions = useCallback(async () => {
    if (!serviceName) {
      setRevisions([]);
      setLoading(false);
      setError(new Error('No Knative service annotation found on entity'));
      return;
    }

    // 仅首次加载时显示 loading，轮询刷新时静默更新
    if (!hasLoadedRef.current) {
      setLoading(true);
    }
    setError(undefined);

    try {
      const targetCluster = getClusterFromAnnotations() || 'ali-idp-prod';

      // Fetch all revisions in the namespace, then filter by service
      const response = await kubernetesApi.proxy({
        clusterName: targetCluster,
        path: `/apis/serving.knative.dev/v1/namespaces/${serviceNamespace}/revisions`,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch Knative Revisions: ${response.statusText}`);
      }

      const data = await response.json();
      const items = (data.items || []) as KnativeRevision[];

      // Filter revisions that belong to this service
      const serviceRevisions = items.filter(revision => {
        const ownerRefs = revision.metadata.ownerReferences || [];
        return ownerRefs.some(
          ref =>
            ref.kind === 'Configuration' &&
            ref.name === serviceName,
        );
      });

      // Sort by creation time (newest first)
      serviceRevisions.sort((a, b) => {
        const timeA = new Date(a.metadata.creationTimestamp).getTime();
        const timeB = new Date(b.metadata.creationTimestamp).getTime();
        return timeB - timeA;
      });

      setRevisions(serviceRevisions);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setRevisions([]);
    } finally {
      setLoading(false);
      hasLoadedRef.current = true;
    }
  }, [kubernetesApi, serviceName, serviceNamespace, clusterName, entity, refreshKey]);

  useEffect(() => {
    fetchRevisions();
  }, [fetchRevisions]);

  // 自动轮询（静默刷新，不重置 loading 状态）
  useEffect(() => {
    if (!refreshIntervalMs || refreshIntervalMs <= 0) return undefined;
    const timer = setInterval(() => {
      setRefreshKey(prev => prev + 1);
    }, refreshIntervalMs);
    return () => clearInterval(timer);
  }, [refreshIntervalMs]);

  const refresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  return { revisions, loading, error, refresh };
}

